// @ts-nocheck
import { SESSION_EVENTS } from '../const/CommonConst';

/**
 * Read a replacement token from storage instead of relying on a tab-local copy.
 *
 * A different nonempty token is a recovery candidate, not proof of server-side
 * validity. The retried API request validates it; no JWT decoding or expiry-clock
 * assumptions are made here. Removing a token is never treated as renewal.
 * This helper assumes tabs belong to the same signed-in account; it does not
 * synchronize user profiles, permissions, impersonation or account switches.
 *
 * @param {string} expiredToken - Token attached to the request that returned 401.
 * @returns {string|null} A different shared token, or null if none is available.
 */
export const getUpdatedAccessToken = (expiredToken) => {
  // A missing snapshot is not a renewal. Treating it as one retries the same
  // expired access token and never calls the refresh endpoint.
  if (!expiredToken) return null;
  const token = localStorage.getItem('access_token');
  return token && token !== expiredToken ? token : null;
};

/**
 * Notify this document that its expiry UI can be dismissed after recovery.
 * localStorage provides cross-tab delivery; this event covers the writing tab,
 * which does not receive a storage event for its own writes.
 * @returns {void}
 */
export const notifySessionRefreshed = () => {
  document.dispatchEvent(new CustomEvent(SESSION_EVENTS.SESSION_REFRESHED));
};

/**
 * Show a session prompt and wait for a user action or a replacement access token.
 *
 * Storage, focus and visibility listeners exist only while this promise is pending.
 * Every user action checks storage synchronously, covering a click/countdown that
 * arrives before a background tab receives its queued storage notification.
 * The caller must recheck storage after awaiting and immediately before logout.
 *
 * @param {string} expiredToken - Token whose failure caused this prompt.
 * @param {string} promptEvent - Document event used to open the appropriate dialog.
 * @param {Object<string, string>} actions - Document event names mapped to results,
 *   e.g. USER_CONFIRMED -> 'confirmed', USER_DECLINED -> 'declined'.
 * @returns {Promise<string>} 'refreshed' on renewal, otherwise the mapped action result.
 */
export const waitForSessionEvent = (expiredToken, promptEvent, actions) => {
  return new Promise((resolve) => {
    let settled = false;
    const listeners = Object.entries(actions).map(([event, result]) => [
      event,
      () => finish(getUpdatedAccessToken(expiredToken) ? 'refreshed' : result),
    ]);

    function checkSession() {
      // Focus/visibility also recover suspended tabs with delayed storage events.
      if (getUpdatedAccessToken(expiredToken)) finish('refreshed');
    }

    function handleStorage(event) {
      // Ignore unrelated preferences and sessionStorage events. Read the current
      // value rather than event.newValue, which may describe an older write.
      if (event.storageArea === localStorage && event.key === 'access_token') {
        checkSession();
      }
    }

    function finish(result) {
      if (settled) return;
      settled = true;
      listeners.forEach(([event, handler]) => {
        document.removeEventListener(event, handler);
      });
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', checkSession);
      document.removeEventListener(
        SESSION_EVENTS.SESSION_REFRESHED,
        checkSession
      );
      // Cleanup must precede the notification: dispatchEvent is synchronous and
      // this wait also listens for SESSION_REFRESHED in the writing document.
      if (result === 'refreshed') notifySessionRefreshed();
      resolve(result);
    }

    listeners.forEach(([event, handler]) => {
      document.addEventListener(event, handler);
    });
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', checkSession);
    document.addEventListener(SESSION_EVENTS.SESSION_REFRESHED, checkSession);

    // Close the gap between detecting 401 and registering these listeners.
    if (getUpdatedAccessToken(expiredToken)) {
      finish('refreshed');
    } else {
      document.dispatchEvent(new CustomEvent(promptEvent));
    }
  });
};

/**
 * Serialize refresh calls across cooperating tabs on the same origin.
 *
 * The browser releases the Web Lock when the callback settles or its tab closes.
 * Only the refresh transaction belongs inside this lock; waiting for a modal
 * while holding it would prevent other tabs from recovering the shared session.
 * When Web Locks is unavailable (including insecure contexts), the callback runs
 * directly. Storage rechecks still protect completed renewals, but simultaneous
 * network refreshes cannot be prevented by this fallback.
 *
 * @template T
 * @param {function(): Promise<T>} refresh - Refresh and persist the token pair.
 * @returns {Promise<T>} The callback result, or its rejection after lock release.
 */
export const withSessionRefreshLock = (refresh) => {
  if (navigator.locks?.request) {
    return navigator.locks.request('vault-session-refresh', refresh);
  }
  return refresh();
};
